import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { User, UserDataTable } from "../../../../apis/User";
import { Loading } from "../../../../components/ui/loading/Loading";
import { Path } from "../../../../components/ui/path/Path";
import firebase from "firebase";

import styles from "./UsersList.module.css";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
} from "@material-ui/core";
import { FormProvider, useForm } from "react-hook-form";
import { TextInput } from "../../../../components/ui/input/textInput/TextInput";
import { useHistory } from "react-router-dom";
import { Card } from "../../../../components/ui/card/Card";
export const UsersList = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [users, setUsers] = useState<any[]>();
    var cursor =
        useRef<
            firebase.firestore.QueryDocumentSnapshot<firebase.firestore.DocumentData>
        >();

    const { t } = useTranslation();

    useEffect(() => {
        document.title = t("Users list");

        getListUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function getListUsers() {
        User.getListAllUsers()
            .then((response) => {
                cursor.current = response.cursor;
                let formattedUsers = response.data.map((user) =>
                    new User(user).getDataTableFormat()
                );
                setUsers(formattedUsers);
            })
            .catch(() => {
                setError(t("Could not obtain the list of users"));
            })
            .finally(() => {
                setLoading(false);
            });
    }

    if (loading) return <Loading />;
    if (error) return <Error error={error} />;

    return (
        <div className={`app-body-container-box`}>
            <Header />
            <EnhancedTable users={users!} />
        </div>
    );
};

const Header = () => {
    const { t } = useTranslation();
    return (
        <Path
            elements={[
                { text: t("Administration"), linkTo: "/administration" },
                {
                    text: t("Users"),
                    linkTo: "/administration/users/users-list",
                },
                {
                    text: t("Users List"),
                    linkTo: "/administration/users/users-list",
                },
            ]}
            className={styles.header}
        />
    );
};

interface EnhancedTableProps {
    onRequestSort: (
        event: React.MouseEvent<unknown>,
        property: keyof UserDataTable
    ) => void;
    order: Order;
    orderBy: string;
}

interface HeadCell {
    disablePadding: boolean;
    id: keyof UserDataTable;
    label: string;
    numeric: boolean;
}

type Order = "asc" | "desc";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key
): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string }
) => number {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

const headCells: HeadCell[] = [
    {
        id: "id",
        numeric: false,
        disablePadding: false,
        label: "User ID",
    },
    {
        id: "childrenNames",
        numeric: false,
        disablePadding: true,
        label: "Children names",
    },
    {
        id: "ages",
        numeric: false,
        disablePadding: false,
        label: "Ages",
    },
    {
        id: "province",
        numeric: false,
        disablePadding: false,
        label: "Province",
    },
    {
        id: "subscriptionType",
        numeric: false,
        disablePadding: false,
        label: "Subscription Type",
    },
];

function EnhancedTableHead(props: EnhancedTableProps) {
    const { order, orderBy, onRequestSort } = props;
    const { t } = useTranslation();
    const createSortHandler =
        (property: keyof UserDataTable) =>
        (event: React.MouseEvent<unknown>) => {
            onRequestSort(event, property);
        };

    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell) => (
                    <TableCell
                        color={"red"}
                        key={headCell.id}
                        align={headCell.numeric ? "right" : "left"}
                        padding={headCell.disablePadding ? "none" : "default"}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : "asc"}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {t(headCell.label)}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

const EnhancedTableToolbar = (props: { onFilterClick: Function }) => {
    const [filter, setFilter] = useState<string | undefined>(undefined);
    const methods = useForm({ mode: "onChange" });

    const { t } = useTranslation();

    // We use useEffect to skip the first render from doing this
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        let currentState = methods.watch(["filter"]).filter;

        if (currentState !== filter) {
            setFilter(currentState);
            props.onFilterClick(currentState);
        }
    });

    return (
        <div className={styles.filterContainer}>
            <FormProvider {...methods}>
                <form>
                    <TextInput
                        placeholder={t("Filter") + "..."}
                        key="filter"
                        name="filter"
                        style={{
                            width: "100%",
                            backgroundColor: "transparent",
                        }}
                    />
                </form>
            </FormProvider>
        </div>
    );
};

const EnhancedTable = (props: { users: UserDataTable[] }) => {
    const [formattedUsers, setFormattedUsers] = useState<UserDataTable[]>([
        ...props.users,
    ]);

    const [order, setOrder] = useState<Order>("asc");
    const [orderBy, setOrderBy] = useState<keyof UserDataTable>("id");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const history = useHistory();
    const { t } = useTranslation();

    function onFilterClick(filter: string) {
        if (filter === "") setFormattedUsers([...props.users]);
        else {
            let filteredUsers = props.users.filter((user) =>
                Object.values(user).find((val) => val.includes(filter))
            );
            setFormattedUsers([...filteredUsers]);
        }
    }
    const handleRequestSort = (
        event: React.MouseEvent<unknown>,
        property: keyof UserDataTable
    ) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const emptyRows =
        rowsPerPage -
        Math.min(rowsPerPage, formattedUsers.length - page * rowsPerPage);

    const goToUserDetails = (id?: string) => {
        if (!id) return;
        history.push(`/administration/users/${id}/`);
    };

    return (
        <>
            <EnhancedTableToolbar onFilterClick={onFilterClick} />
            <Card className={styles.cardContainer}>
                <TableContainer className={styles.tableContainer}>
                    <Table size="medium" stickyHeader>
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                        />
                        <TableBody>
                            {stableSort(
                                formattedUsers,
                                getComparator(order, orderBy)
                            )
                                .slice(
                                    page * rowsPerPage,
                                    page * rowsPerPage + rowsPerPage
                                )
                                .map((row, index) => {
                                    const fRow: UserDataTable =
                                        row as UserDataTable;
                                    return (
                                        <TableRow
                                            hover
                                            tabIndex={-1}
                                            key={fRow.id}
                                            onClick={() => {
                                                goToUserDetails(fRow.id);
                                            }}
                                        >
                                            <TableCell
                                                component="th"
                                                id={index.toString()}
                                                scope="fRow"
                                                padding="none"
                                            >
                                                {t(fRow.id)}
                                            </TableCell>
                                            <TableCell>
                                                {t(fRow.childrenNames)}
                                            </TableCell>
                                            <TableCell>
                                                {t(fRow.ages)}
                                            </TableCell>
                                            <TableCell>
                                                {t(fRow.province)}
                                            </TableCell>
                                            <TableCell>
                                                {t(fRow.subscriptionType)}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            {emptyRows > 0 && (
                                <TableRow
                                    style={{
                                        height: 53 * emptyRows,
                                    }}
                                >
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={formattedUsers.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                />
            </Card>
        </>
    );
};

const Error = (props: { error: string }) => {
    return <h1>{props.error}</h1>;
};
